import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { EmpresaService } from '../../../core/services/empresa.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Empresa } from '../../../core/models/empresa';
import { Assinatura } from '../../../core/models/assinatura';

@Component({
  selector: 'app-empresa-form',
  templateUrl: './empresa-form.component.html',
  styleUrls: ['./empresa-form.component.scss']
})
export class EmpresaFormComponent implements OnInit {
  form!: FormGroup;
  editMode = false;
  id?: string | null;
  loading = false;
  empresas: Empresa[] = []; // Lista de empresas para a tabela

  statusAssinaturaOptions: { label: string; value: Assinatura['statusAssinatura'] }[] = [
    { label: 'Trial', value: 'TRIAL' },
    { label: 'Ativa', value: 'ATIVA' },
    { label: 'Vencida', value: 'VENCIDA' },
    { label: 'Bloqueada', value: 'BLOQUEADA' },
    { label: 'Suspensa', value: 'SUSPENSA' },
    { label: 'Cancelada', value: 'CANCELADA' }
  ];

  planoAssinaturaOptions = [
    { label: 'Trial', value: 'TRIAL' },
    { label: 'Plano Mensal', value: 'MENSAL' },
    { label: 'Plano Trimestral', value: 'TRIMESTRAL' },
    { label: 'Plano Semestral', value: 'SEMESTRAL' },
    { label: 'Plano Anual', value: 'ANUAL' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: EmpresaService,
    private router: Router,
    private route: ActivatedRoute,
    private message: MessageService,
    private confirmation: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.loadEmpresas(); // Carrega a lista ao iniciar

    this.id = this.route.snapshot.paramMap.get('id');
    if (this.id && this.id !== 'cadastrar') {
      this.editMode = true;
      this.loadEmpresa(this.id);
    }
  }

  createForm() {
    this.form = this.fb.group({
      tipoPessoa: ['JURIDICA', Validators.required],
      nomeRazao: ['', Validators.required],
      cpfCnpj: ['', Validators.required],
      rgIE: [''],
      email: ['', [Validators.required, Validators.email]],
      celular: [''],
      telefone: [''],
      contato: [''],
      cep: [''],
      endereco: [''],
      numero: [''],
      bairro: [''],
      cidade: [''],
      estado: [''],
      logo1: [''],
      logo2: [''],
      facebook: [''],
      instagram: [''],
      dataAquisicao: [null],
      dataVencimento: [null],
      diasTrial: [0],
      ativo: [true],
      statusAssinatura: ['TRIAL'],
      planoAssinatura: ['MENSAL'] // Novo campo com valor padrão
    });
  }

  loadEmpresas() {
    this.loading = true;
    this.service.listar().subscribe({
      next: (res) => {
        this.empresas = res;
        this.loading = false;
      },
      error: (err) => {
        this.message.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar lista de empresas' });
        this.loading = false;
      }
    });
  }

  loadEmpresa(id: string) {
    this.loading = true;
    this.service.obterPorId(id).subscribe({
      next: (res) => {
        this.form.patchValue(res);
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar empresa:', err);
        this.message.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar empresa' });
        this.loading = false;
      }
    });
  }

  salvar() {
    if (this.form.invalid) {
      this.message.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha os campos obrigatórios' });
      return;
    }

    const empresa = this.form.value as Empresa;
    this.loading = true;

    if (this.editMode && this.id) {
      this.service.atualizar(this.id, empresa).subscribe({
        next: () => {
          this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Empresa atualizada' });
          this.loadEmpresas(); // Atualiza a lista
          this.limparFormulario();
        },
        error: (err) => {
          this.handleError(err, 'Falha ao atualizar');
          this.loading = false;
        }
      });
    } else {
      this.service.cadastrar(empresa).subscribe({
        next: () => {
          this.message.add({ severity: 'success', summary: 'Sucesso', detail: 'Empresa cadastrada' });
          this.loadEmpresas(); // Atualiza a lista
          this.limparFormulario();
        },
        error: (err) => {
          this.handleError(err, 'Falha ao cadastrar');
          this.loading = false;
        }
      });
    }
  }

  handleError(err: any, defaultMsg: string) {
    console.error(defaultMsg, err);
    let detail = defaultMsg;
    if (err.error && typeof err.error === 'string') {
        detail = err.error;
    } else if (err.error && err.error.message) {
      detail = err.error.message;
    } else if (err.message) {
      detail = err.message;
    }
    this.message.add({ severity: 'error', summary: 'Erro', detail: detail });
  }

  cancelar() {
    this.limparFormulario();
  }

  limparFormulario() {
    this.editMode = false;
    this.id = null;
    this.form.reset();
    // Restaurar valores padrão
    this.form.patchValue({
      tipoPessoa: 'JURIDICA',
      statusAssinatura: 'TRIAL',
      planoAssinatura: 'MENSAL',
      diasTrial: 0,
      ativo: true
    });
    this.loading = false;
  }

  // --- Ações da Tabela ---

  editar(empresa: Empresa) {
    if (!empresa.id) return;
    this.editMode = true;
    this.id = empresa.id;
    // Carregar os dados no formulário
    this.form.patchValue(empresa);
    // Rola a página para o topo para editar
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  confirmarAtivarDesativar(empresa: Empresa) {
    if (!empresa.id) return;
    const willDeactivate = !!empresa.ativo;
    const actionLabel = willDeactivate ? 'desativar' : 'ativar';

    this.confirmation.confirm({
      message: `Confirma ${actionLabel} a empresa ${empresa.nomeRazao}?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.service.ativarDesativar(empresa.id!, !willDeactivate).subscribe({
          next: (res: Empresa) => {
            const idx = this.empresas.findIndex((e) => e.id === empresa.id);
            if (idx >= 0) this.empresas[idx] = res;
            this.message.add({ severity: 'success', summary: 'Sucesso', detail: `Empresa ${willDeactivate ? 'desativada' : 'ativada'} com sucesso` });
          },
          error: (err: any) => {
            this.message.add({ severity: 'error', summary: 'Erro', detail: `Falha ao ${actionLabel} empresa` });
          },
        });
      },
    });
  }

  // --- Lógica de Tipo Pessoa ---

  onTipoPessoaChange(): void {
    this.form.get('cpfCnpj')?.setValue('');
  }

  get isFisica(): boolean {
    return this.form.get('tipoPessoa')?.value === 'FISICA';
  }

  get idMask(): string {
    return this.isFisica ? '000.000.000-00' : '00.000.000/0000-00';
  }

  get idLabel(): string {
    return this.isFisica ? 'CPF' : 'CNPJ';
  }
}
